import { useEffect, useState } from 'react';

import { useLocation } from 'react-router';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';

import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Divider } from '@vk-hr-tek/ui/Divider';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { Icon } from '@vk-hr-tek/ui/Icon';

import { useSelector } from '@app/hooks';
import { UserRoleEnum } from '@app/types';

import { selectUser, selectStatus } from '../../../../user';
import { SidebarContext, useSidebarContext } from '../SidebarContext';
import { ProcessedChildMenuItem } from '../../../types';
import { SidebarMappedItem } from '../SidebarMappedItem';

import { useStyles } from './SidebarMenu.styles';

const ITEM_HEIGHT = 40;

interface SidebarMenuProps {
  menuLabel: string;
  maxItemsInMenu?: number;
  items: ProcessedChildMenuItem[];
}

export const SidebarMenu = ({
  menuLabel,
  maxItemsInMenu = 5,
  items,
}: SidebarMenuProps) => {
  const { sidebarOpened, onMenuItemClick } = useSidebarContext();
  const isSkeletonLoading = useSelector(selectStatus) === 'loading';
  const { pathname } = useLocation();
  const user = useSelector(selectUser);
  const userId = user?.id || '';

  const handleIsActive = (path: string, href: string): boolean => {
    const userRole = href.split('/')[1] || UserRoleEnum.Employee;

    if (href.startsWith('/personal')) {
      const currentUserId = pathname.split('/personal/')[1] || '';
      return path.startsWith('/personal') && userId === currentUserId;
    }

    if (href === `/${userRole}/events`) {
      return (
        path === `/${userRole}/events` ||
        (path.startsWith(`/${userRole}/events/`) && !path.includes('/paper'))
      );
    }

    if (href === `/${userRole}/settings/attorneys`) {
      return (
        path === `/${userRole}/settings/attorneys` ||
        (path.startsWith(`/${userRole}/settings/attorneys/`) &&
          !path.includes('/attorneys-users'))
      );
    }

    return path === href || path.startsWith(`${href}/`);
  };

  const [allElementsVisible, setAllElementsVisible] = useState<boolean>(false);
  const [submenusVisibleItemsCount, setSubmenusVisibleItemsCount] =
    useState<number>(0);

  const handleSetSubmenuItemsCount = (count: number) => {
    setSubmenusVisibleItemsCount((prev) => prev + count);
  };

  // eslint-disable-next-line no-magic-numbers
  const roleTitleFontSize = sidebarOpened ? 14 : 1;

  const itemsInMenu =
    items.length > maxItemsInMenu ? maxItemsInMenu : items.length;

  const menuHeight =
    ((allElementsVisible ? items.length : itemsInMenu) +
      submenusVisibleItemsCount) *
    ITEM_HEIGHT;

  const { classes } = useStyles({
    menuHeight,
    sidebarOpened,
    roleTitleFontSize,
    allElementsVisible,
  });

  const handleAllElementsVisibleState = () => {
    setAllElementsVisible((prev) => !prev);
  };

  const toggleButtonText = allElementsVisible
    ? 'Показать меньше'
    : `Показать еще ${items.length - maxItemsInMenu}`;

  useEffect(() => {
    if (!sidebarOpened) {
      setSubmenusVisibleItemsCount(0);
    }
  }, [sidebarOpened]);

  useEffect(() => {
    if (!allElementsVisible) {
      setSubmenusVisibleItemsCount(0);
    }
  }, [allElementsVisible]);

  return (
    <Box display="flex" flexDirection="column" gap="4">
      <Box className={classes.sidebarRoleTitle}>
        {sidebarOpened ? (
          <Typography variant="inherit" noWrap color="text.light.tertirary">
            {isSkeletonLoading ? <Skeleton width={200} /> : menuLabel}
          </Typography>
        ) : (
          <Box width="100%">
            <Divider />
          </Box>
        )}
      </Box>

      <SidebarContext
        value={{
          sidebarOpened,
          onMenuItemClick,
          isActive: handleIsActive,
          setSubmenusVisibleItemsCount: handleSetSubmenuItemsCount,
        }}
      >
        <Box className={classes.sidebarLinks}>
          {items
            .slice(0, allElementsVisible ? items.length : itemsInMenu)
            .map((item) => {
              return <SidebarMappedItem key={item.id} item={item} />;
            })}
        </Box>
      </SidebarContext>

      {items.length > maxItemsInMenu && (
        <Box
          className={classNames(classes.showAllButton, 'aqa_showAllButton')}
          onClick={handleAllElementsVisibleState}
        >
          <Tooltip
            title={toggleButtonText}
            placement="right"
            disabled={sidebarOpened}
          >
            <Box
              p="8"
              display="flex"
              minWidth={40}
              minHeight={40}
              alignItems="center"
              justifyContent="center"
              fontSize="20px"
              className={classes.expandMoreIcon}
            >
              {isSkeletonLoading ? (
                <Skeleton width="100%" />
              ) : (
                <Icon
                  name="chevron_down"
                  size="20"
                  color="original.brand.primary"
                />
              )}
            </Box>
          </Tooltip>

          <Box className={classes.showAllButtonText}>
            <Typography variant="body3" color="text.light.tertirary" noWrap>
              {toggleButtonText}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
